import { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Checkbox, Input, Typography, Flex } from 'antd';
import type { InputRef } from 'antd';
import { SafetyOutlined, KeyOutlined } from '@ant-design/icons';
import styles from './MFAVerify.module.css';

const { Text, Title } = Typography;

const CODE_LENGTH = 6;

interface MFAVerifyProps {
  onVerify: (code: string) => void;
  loading?: boolean;
  error?: string;
  onBack?: () => void;
  showRememberDevice?: boolean;
  rememberDevice?: boolean;
  onRememberDeviceChange?: (checked: boolean) => void;
  /** Extra line under the main instructions (e.g. trusted-device help or API detail). */
  instructionHint?: string;
}

export const MFAVerify = ({
  onVerify,
  loading,
  error,
  onBack,
  showRememberDevice,
  rememberDevice,
  onRememberDeviceChange,
  instructionHint,
}: MFAVerifyProps) => {
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const backupInputRef = useRef<InputRef>(null);
  const onVerifyRef = useRef(onVerify);
  onVerifyRef.current = onVerify;
  const lastAutoSubmittedRef = useRef<string | null>(null);

  useEffect(() => {
    if (useBackupCode) {
      backupInputRef.current?.focus();
    } else {
      inputRefs.current[0]?.focus();
    }
  }, [useBackupCode]);

  // Auto-submit when 6 digits are present — use effect so we read committed state (fixes
  // React 18 batching / stale `digits` in onChange) and dedupe Strict Mode double-invoke.
  useEffect(() => {
    if (useBackupCode) return;
    const code = digits.join('');
    if (code.length !== CODE_LENGTH) {
      lastAutoSubmittedRef.current = null;
      return;
    }
    if (error) {
      lastAutoSubmittedRef.current = null;
      return;
    }
    if (lastAutoSubmittedRef.current === code) return;
    lastAutoSubmittedRef.current = code;
    onVerifyRef.current(code);
  }, [digits, useBackupCode, error]);

  const submitCode = useCallback((newDigits: string[]) => {
    const code = newDigits.join('');
    if (code.length === CODE_LENGTH) {
      onVerifyRef.current(code);
    }
  }, []);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    setDigits((prev) => {
      const newDigits = [...prev];

      if (value.length > 1) {
        const pasted = value.slice(0, CODE_LENGTH).split('');
        pasted.forEach((char, i) => {
          if (index + i < CODE_LENGTH) newDigits[index + i] = char;
        });
        queueMicrotask(() => {
          const nextIndex = Math.min(index + pasted.length, CODE_LENGTH - 1);
          inputRefs.current[nextIndex]?.focus();
        });
        return newDigits;
      }

      newDigits[index] = value;
      queueMicrotask(() => {
        if (value && index < CODE_LENGTH - 1) {
          inputRefs.current[index + 1]?.focus();
        }
      });
      return newDigits;
    });
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleModeSwitch = () => {
    setUseBackupCode((prev) => !prev);
    setBackupCode('');
    setDigits(Array(CODE_LENGTH).fill(''));
  };

  const handleBackupSubmit = () => {
    const trimmed = backupCode.trim();
    if (trimmed) {
      onVerify(trimmed);
    }
  };

  return (
    <Flex vertical align="center" gap={24} className={styles.container}>
      {useBackupCode ? (
        <KeyOutlined className={styles.icon} />
      ) : (
        <SafetyOutlined className={styles.icon} />
      )}

      <div className={styles.header}>
        <Title level={4} className={styles.title}>
          Two-factor authentication
        </Title>
        <Text type="secondary">
          {useBackupCode
            ? 'Enter one of your backup codes.'
            : 'Enter the 6-digit code from your authenticator app.'}
        </Text>
        {instructionHint && (
          <Text type="secondary" className={styles.instructionHint}>
            {instructionHint}
          </Text>
        )}
      </div>

      {useBackupCode ? (
        <Input
          ref={backupInputRef}
          value={backupCode}
          onChange={(e) => setBackupCode(e.target.value)}
          onPressEnter={handleBackupSubmit}
          placeholder="e.g. abc12-def34"
          className={styles.backupInput}
          disabled={loading}
          autoComplete="off"
        />
      ) : (
        <>
          {/* eslint-disable react/no-array-index-key -- fixed-length, non-reorderable OTP inputs */}
          <Flex gap={8} className={styles.codeInputs}>
            {digits.map((digit, i) => (
              <input
                key={`digit-${i}`}
                ref={(el) => {
                  inputRefs.current[i] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={CODE_LENGTH}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className={styles.digitInput}
                autoComplete="one-time-code"
                disabled={loading}
              />
            ))}
          </Flex>
          {/* eslint-enable react/no-array-index-key */}
        </>
      )}

      {error && <Text type="danger">{error}</Text>}

      {showRememberDevice && (
        <Checkbox
          checked={rememberDevice}
          onChange={(e) => onRememberDeviceChange?.(e.target.checked)}
          disabled={loading}
        >
          Remember this device
        </Checkbox>
      )}

      <Button
        type="primary"
        loading={loading}
        disabled={
          useBackupCode
            ? !backupCode.trim()
            : digits.join('').length < CODE_LENGTH
        }
        onClick={useBackupCode ? handleBackupSubmit : () => submitCode(digits)}
        className={styles.verifyButton}
      >
        Verify
      </Button>

      <Button type="link" onClick={handleModeSwitch} disabled={loading}>
        {useBackupCode
          ? 'Use authenticator app instead'
          : "Can't access your device? Use a backup code"}
      </Button>

      {onBack && (
        <Button type="link" onClick={onBack} disabled={loading}>
          Back to login
        </Button>
      )}
    </Flex>
  );
};
